import { Observable, of, throwError } from 'rxjs';
import {
  Certificate, CertificateSelectionResponse, SignatureProviderConfig, SignatureProviderResponse,
  SignatureRequest, SignatureResponse, SignatureStatus
} from '../signature.objects';
import { BaseSignatureProvider } from './signature.provider';
import { TranslateService } from '@ngx-translate/core';
import { PlycaDataService } from '../../plyca.service';
import { UserService } from '../../../app/user.service';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { DialogService, DynamicDialogConfig } from 'primeng-lts/dynamicdialog';
import { CertificateSelection } from '../components/certificate-selection.component';
import { SignaturePinComponent } from '../components/pin.component';
import { WaitLoadingService } from '../../../app/components/common/waitLoading/wait-loading.service';
import { SignatureUtils } from '../../signature.utils';
import { PlycaErrorHandler } from '../../../http/PlycaErrorHandler';
import { FileDetailsDTO } from '../../../app/api/plyca-dtos';
import { Configuration } from '../../../app/conf/app.constants';

const AUTHENTICATION_PROPERTY = 'signatureProviderAuth';

export class SignatureAuthorizationResponse implements SignatureProviderResponse {
  authorization: any;
  status: SignatureStatus;
  errorMessage: string;
}

export class AuthorizationError extends Error {
  constructor(msg?) {
    super(msg);
    Object.setPrototypeOf(this, AuthorizationError.prototype);
  }
}

export class CancelOperationnError extends Error {
  constructor(msg?) {
    super(msg);
    Object.setPrototypeOf(this, CancelOperationnError.prototype);
  }
}

export class AuthenticatedProviderConfig extends SignatureProviderConfig {
  /**
   * Se requiere PIN para firmar
   */
  pinToSign: boolean;
  /**
   * Si solo hay un certificado dispoible seleccionar automaticamente
   */
  autoSelectCertificate: boolean;
}

/**
 * Clase base de proveedor de firma que requiere de autenticacion previa y almacena
 * en el localstorage las credenciales de conexion
 */
export abstract class AuthenticatedSignatureProvider extends BaseSignatureProvider {

  private pinCertificateMap: Map<string, string>;

  constructor(plycaService: PlycaDataService, translateService: TranslateService, protected userService: UserService,
    protected dialogService: DialogService, protected waitService: WaitLoadingService, signatureUtils: SignatureUtils
    , private plycaErrorHandler: PlycaErrorHandler, protected configuration: Configuration) {

    super(plycaService, translateService, signatureUtils);
    this.pinCertificateMap = new Map();
  }

  /**
   * Metodo de autenticacion para acceder al servicio de firma
   * @returns credenciales de acceso al servicio
   */
  protected abstract authenticateInternal(): Observable<SignatureAuthorizationResponse>;
  /**
   * Metodo que indica si se requiere pin para realizar la firma
   */
  private isPinRequiredToSign(): boolean {
    const providerConfig = this.getProviderConfig() as AuthenticatedProviderConfig;
    return (providerConfig?.pinToSign) ? providerConfig.pinToSign : true;
  }

  /**
   * Metodo de firma de un fichero
   *
   * @param request datos de firma
   * @returns resultado de la firma realizada
   */
  protected abstract signInternal(request: SignatureRequest, authorization: any): Observable<FileDetailsDTO>;

  /**
   * Metodo de encargado de la selección de un certificado
   *
   * @param authorization
   * @return listado de certificados
   */
  public abstract getCertificateList(): Observable<Certificate[]>;

  /**
   * Metodo que indica si se hace seleccion automatica de un certificado en caso
   * de que el metodo getCertificateList devuelva un solo certificado
   */
  private isAutoSelectCertificate(): boolean {
    const providerConfig = this.getProviderConfig() as AuthenticatedProviderConfig;
    return (providerConfig?.autoSelectCertificate) ? providerConfig.autoSelectCertificate : true;
  }
  /**
   * Metodo de configuracion de la ventana de solicitud de credenciales para identificarse en el
   * proveedor de firma
   */
  public getProviderConfig(): SignatureProviderConfig {
    return this.signatureUtils.getSignatureProvider(this.getSignatureProviderType()).config as SignatureProviderConfig;
  }

  protected isAuthenticated(): boolean {
    return this.getAuthorizationResponse() !== undefined;
  }

  public authenticate(): Observable<SignatureAuthorizationResponse> {
    return this.authenticateInternal().pipe(
      map((response: SignatureAuthorizationResponse) => {
        // almacenar credenciales
        this.userService.setUserExtraParams(AUTHENTICATION_PROPERTY,
          (response?.status === SignatureStatus.OK) ? response.authorization : undefined);
        return (response) ? response : this.getCancelSignatureResponse() as SignatureAuthorizationResponse;
      })
    );
  }
  protected getAuthorizationResponse(): any {
    return this.userService.getSessionInfo()?.extraParams?.[AUTHENTICATION_PROPERTY];
  }

  public selectCertificate(): Observable<CertificateSelectionResponse> {
    return this.executeAuthentication().pipe(
      switchMap((authentication: SignatureAuthorizationResponse) => {
        let obs: Observable<CertificateSelectionResponse>;
        if (authentication.status === SignatureStatus.OK) {
          obs = this.getCertificateList().pipe(
            switchMap((certificates: Certificate[]) => {
              this.waitService.cancelWaiting();
              if (certificates?.length === 1 && this.isAutoSelectCertificate()) {
                const response = new CertificateSelectionResponse();
                response.certificate = certificates[0];
                response.status = SignatureStatus.OK;
                return of(response);
              } else {
                const config: DynamicDialogConfig = {
                  showHeader: true,
                  header: 'Selección certificado',
                  width: '70%',
                  style: { overflow: 'hidden', 'max-height': '90vh' },
                  data: {
                    certificates: certificates
                  }
                };
                const dialogRef = this.dialogService.open(CertificateSelection, config);
                return dialogRef.onClose.pipe(
                  map((certificate: Certificate) => {
                    const selection = new CertificateSelectionResponse();
                    selection.certificate = certificate;
                    selection.status = (certificate) ? SignatureStatus.OK : SignatureStatus.CANCELLED;
                    return selection;
                  })
                );
              }
            }),
            catchError((error: any) => {
              this.plycaErrorHandler.showServerError(error);
              return throwError(error);
            })
          );
        } else {
          const selection = new CertificateSelectionResponse();
          selection.errorMessage = authentication.errorMessage;
          selection.status = authentication.status;
          obs = of(selection);
        }
        return obs;
      })
    )
  }

  public sign(request: SignatureRequest): Observable<SignatureResponse> {
    let obs: Observable<SignatureResponse>;
    if (!request.certificate) {
      // seleccionar certificado
      obs = this.selectCertificate().pipe(
        switchMap((selectedCert: CertificateSelectionResponse) => {
          let internalObs: Observable<SignatureResponse>;
          if (selectedCert?.status === SignatureStatus.OK) {
            // si certificado seleccionado -> firmar
            request.certificate = selectedCert.certificate;
            internalObs = this.signWithCertificate(request);
          } else if (!selectedCert || selectedCert.status === SignatureStatus.CANCELLED) {
            let response = this.getCancelSignatureResponse() as SignatureResponse;
            response.request = request;
            internalObs = of(response);
          }
          return internalObs;
        })
      );
    } else {
      obs = this.executeAuthentication().pipe(
        switchMap((authentication: SignatureAuthorizationResponse) => {
          let internalObs: Observable<SignatureResponse>;
          if (authentication.status === SignatureStatus.OK) {
            internalObs = this.signWithCertificate(request);
          } else {
            const selection = new SignatureResponse();
            selection.errorMessage = authentication.errorMessage;
            selection.status = authentication.status;
            internalObs = of(selection);
          }
          return internalObs;
        })
      )
    }
    return obs;
  }

  protected signWithCertificate(request: SignatureRequest): Observable<SignatureResponse> {
    const authorization = this.getAuthorizationResponse();
    let obs: Observable<FileDetailsDTO>;
    let certificate = request.certificate;
    if (this.isPinRequiredToSign()) {
      obs = this.getPin(certificate).pipe(
        switchMap((pin: string) => {
          let internalObs: Observable<FileDetailsDTO>;
          if (pin) {
            certificate.password = pin;
            // guardar pin con autorizacion
            this.storeCertificatePin(certificate, pin);
            internalObs = this.signInternal(request, authorization);
          } else {
            internalObs = throwError(new CancelOperationnError());
          }
          return internalObs;
        })
      );
    } else {
      obs = this.signInternal(request, authorization);
    }
    return obs.pipe(
      switchMap((signature: FileDetailsDTO) => {
        const params = {
          'expand': 'signatures,signatures.verification'
        }
        return this.plycaService.get<FileDetailsDTO>(this.configuration.FILEDETAILS_SERVICE_URL, signature.identifier, params)
      }),
      map((fileDetails: FileDetailsDTO) => {
        // TODO refactor
        let response = new SignatureResponse();
        response.request = request;
        response.status = SignatureStatus.OK;
        response.file = fileDetails;
        const errSignature = this.signatureUtils.getFirstSignatureWithVerificationEror(fileDetails.signatures)
        if (errSignature) {
          response.errorMessage = (errSignature?.verification?.validationMessages?.length > 0) ?
            errSignature.verification.validationMessages[0] : undefined;
          response.signatureStatus = this.signatureUtils.getValidOrEmptySignatureCode(errSignature);
        } else {
          const lastSignature = this.signatureUtils.getLastSignature(fileDetails.signatures);
          response.signatureStatus = this.signatureUtils.getValidOrEmptySignatureCode(lastSignature);
        }
        return response;
      }),
      catchError((error: any) => {
        let internalObs: Observable<SignatureResponse>;
        if (error instanceof CancelOperationnError) {
          let response = this.getCancelSignatureResponse() as SignatureResponse;
          response.request = request;
          internalObs = of(response);
        } else if (error instanceof CertificateSelectionResponse) {
          internalObs = of({
            status: SignatureStatus.ERROR,
            errorMessage: 'Error al seleccionar certificado',
            request: request
          } as SignatureResponse);
        } else {
          internalObs = throwError(error);
        }
        return internalObs;
      })
    );
  }
  protected storeCertificatePin(certificate: Certificate, pin: string): void {
    this.pinCertificateMap.set(certificate.identifier, pin);
  }

  protected getStoredCertificatePin(certificate: Certificate): string {
    return this.pinCertificateMap.get(certificate.identifier);
  }

  private executeAuthentication(reattemptAuthentication = true): Observable<SignatureAuthorizationResponse> {
    let finalObs: Observable<SignatureAuthorizationResponse>;
    if (!this.isAuthenticated()) {
      finalObs = this.authenticate().pipe(
        switchMap((response: SignatureAuthorizationResponse) => {
          let obs: Observable<SignatureAuthorizationResponse>;
          if (!response) {
            obs = of(this.getCancelSignatureResponse() as SignatureAuthorizationResponse);
          } else {
            switch (response.status) {
              case SignatureStatus.ERROR: {
                // si hay error repetir autenticacion
                obs = this.executeAuthentication();
                break;
              }
              default: {
                obs = of(response);
              }
            }
          }
          return obs;
        }),
        catchError((error: any) => {
          let errroObs: Observable<any>;
          if (error instanceof AuthorizationError) {
            if (reattemptAuthentication) {
              // eliminar credenciales del storage
              this.userService.removeUserExtraParam(AUTHENTICATION_PROPERTY);
              // reintentar autenticacion
              errroObs = this.executeAuthentication();
            } else {
              console.error(error);
              errroObs = throwError(new AuthorizationError());
            }
          } else {
            errroObs = throwError(error);
          }
          return errroObs;
        })
      );
    } else {
      const authResponse = new SignatureAuthorizationResponse();
      authResponse.authorization = this.getAuthorizationResponse();
      authResponse.status = SignatureStatus.OK
      finalObs = of(authResponse);
    }
    return finalObs;
  }

  private getCancelSignatureResponse(): SignatureProviderResponse {
    return {
      status: SignatureStatus.CANCELLED,
      errorMessage: 'Operación cancelada por el usuario'
    };
  }

  protected getPin(certificate: Certificate): Observable<string> {
    let obs: Observable<string>;
    const pin = this.getStoredCertificatePin(certificate);
    if (!pin) {
      const config: DynamicDialogConfig = {
        showHeader: true,
        header: 'Pin certificado',
        width: '40%',
        style: { overflow: 'hidden', 'max-height': '60vh' },
        data: {
          authProvider: this,
          user: this.userService.getUser()
        }
      };
      const dialogRef = this.dialogService.open(SignaturePinComponent, config);
      obs = dialogRef.onClose;
    } else {
      obs = of(pin).pipe(take(1));
    }
    return obs
  }

}
